// src/components/JSONKeyRenamer.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaUpload,
  FaCopy,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
  FaFileExport,
  FaUndo,
} from "react-icons/fa";

// For advanced JSON repair logic:
import { jsonrepair } from "jsonrepair";
import JSON5 from "json5";
import { js_beautify as jsBeautify } from "js-beautify";

// For side-by-side diff
import { diff_match_patch as DiffMatchPatch } from "diff-match-patch";

/**
 * JSONKeyRenamer:
 * 1) Accepts a JSON object
 * 2) Accepts a key mapping { "oldKey": "newKey" }
 * 3) Renames keys in the entire JSON structure
 * 4) Includes advanced features:
 *    - line numbers
 *    - file upload for JSON and mapping
 *    - dark mode
 *    - fix JSON logic
 *    - undo last rename
 *    - key collision detection
 *    - partial mapping summary
 *    - side-by-side diff of renamed keys
 *    - advanced panel toggle
 */
const JSONKeyRenamer = () => {
  // ---------- States ----------
  const [jsonInput, setJsonInput] = useState("");
  const [keyMappingInput, setKeyMappingInput] = useState("");
  const [renamedJSON, setRenamedJSON] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Advanced features toggles
  const [showLineNumbersJson, setShowLineNumbersJson] = useState(true);
  const [showLineNumbersMapping, setShowLineNumbersMapping] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Additional states for collisions and partial summary
  const [collisions, setCollisions] = useState([]);
  const [unusedMappings, setUnusedMappings] = useState([]);
  const [oldJSONBeforeRename, setOldJSONBeforeRename] = useState(""); // For undo / diff

  // For “undo last rename” – store the last good JSON
  const [renameHistory, setRenameHistory] = useState([]);

  // For side-by-side diff
  const [showDiff, setShowDiff] = useState(false);
  const [diffHtml, setDiffHtml] = useState("");

  // Refs for text areas + line numbers
  const jsonTextRef = useRef(null);
  const jsonLinesRef = useRef(null);
  const mappingTextRef = useRef(null);
  const mappingLinesRef = useRef(null);

  // Ref for final output
  const renamedOutputRef = useRef(null);

  // Example data
  const exampleJSON = `{
  "name": "SFDC247",
  "age": 30,
  "address": {
    "city": "New York",
    "zip": "10001"
  },
  "skills": ["Salesforce", "Agentforce"],
  "extra": "collide"
}`;

  const exampleKeyMapping = `{
  "name": "fullName",
  "age": "years",
  "city": "locationCity",
  "notUsed": "whatever",
  "extra": "fullName"
}`;

  // ---------- Dark Mode effect ----------
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // ---------- Create line numbers ----------
  const createLineNumbers = (text) => text.split("\n").map((_, i) => i + 1);

  const [jsonLines, setJsonLines] = useState([]);
  const [mappingLines, setMappingLines] = useState([]);

  useEffect(() => {
    if (showLineNumbersJson) {
      setJsonLines(createLineNumbers(jsonInput));
    }
  }, [jsonInput, showLineNumbersJson]);

  useEffect(() => {
    if (showLineNumbersMapping) {
      setMappingLines(createLineNumbers(keyMappingInput));
    }
  }, [keyMappingInput, showLineNumbersMapping]);

  // ---------- parse line/column from error message ----------
  const extractLineColumn = (message) => {
    const match = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (match) {
      return { line: match[1], col: match[2] };
    }
    return null;
  };

  // ---------- rename keys logic ----------
  const renameKeys = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        let jsonObject;
        try {
          jsonObject = JSON.parse(jsonInput);
        } catch (parseErr) {
          handleParseError(parseErr, "JSON");
          return;
        }

        let keyMapping;
        try {
          keyMapping = JSON.parse(keyMappingInput);
        } catch (mapErr) {
          handleParseError(mapErr, "Key Mapping");
          return;
        }

        if (
          typeof keyMapping !== "object" ||
          keyMapping === null ||
          Array.isArray(keyMapping)
        ) {
          throw new Error("Key mapping must be a valid JSON object.");
        }

        // store old for undo/diff
        setOldJSONBeforeRename(jsonInput);

        // rename
        const { renamedObj, collisionKeys, usedMappingKeys } = renameKeysInObject(
          jsonObject,
          keyMapping
        );

        // convert to string
        const result = JSON.stringify(renamedObj, null, 2);
        setRenamedJSON(result);

        // collision detection
        setCollisions(collisionKeys);
        // partial / unused mapping detection
        const allKeys = Object.keys(keyMapping);
        const unused = allKeys.filter((k) => !usedMappingKeys.has(k));
        setUnusedMappings(unused);

        // store in history for undo
        setRenameHistory((prev) => [...prev, jsonInput]);

        setError("");
        setCopySuccess(false);
        setShowDiff(false); // reset side-by-side diff if it was on
      } catch (err) {
        setRenamedJSON("");
        setCollisions([]);
        setUnusedMappings([]);
        setError(err.message || "Invalid JSON or key mapping.");
        setCopySuccess(false);
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  const handleParseError = (parseErr, label) => {
    const info = extractLineColumn(parseErr.message);
    if (info) {
      setError(
        `Invalid ${label} near line ${info.line}, column ${info.col}: ${parseErr.message}`
      );
    } else {
      setError(parseErr.message || `Invalid ${label} input.`);
    }
    setRenamedJSON("");
    setCollisions([]);
    setUnusedMappings([]);
    setCopySuccess(false);
    setProcessing(false);
  };

  const renameKeysInObject = (obj, keyMapping) => {
    const collisionKeys = [];
    const usedMappingKeys = new Set();

    // recursively rename
    const renameHelper = (o) => {
      if (typeof o !== "object" || o === null) return o;

      if (Array.isArray(o)) {
        return o.map((item) => renameHelper(item));
      }

      const renamedObj = {};
      for (const key in o) {
        const newKey = keyMapping[key] || key;

        // track usage if a mapping was found
        if (keyMapping[key]) {
          usedMappingKeys.add(key);
        }

        // detect collisions if renamedObj[newKey] already exists
        if (Object.prototype.hasOwnProperty.call(renamedObj, newKey)) {
          collisionKeys.push(newKey);
        }
        renamedObj[newKey] = renameHelper(o[key]);
      }
      return renamedObj;
    };

    const result = renameHelper(obj);
    return { renamedObj: result, collisionKeys, usedMappingKeys };
  };

  // ---------- partial mapping summary, collision, old JSON storage ----------

  // ---------- Clear ----------
  const clearInputs = () => {
    setJsonInput("");
    setKeyMappingInput("");
    setRenamedJSON("");
    setError("");
    setCopySuccess(false);
    setCollisions([]);
    setUnusedMappings([]);
    setShowDiff(false);
    setRenameHistory([]);
  };

  // ---------- Undo last rename ----------
  const handleUndo = () => {
    if (renameHistory.length < 1) {
      setError("No previous rename to undo!");
      return;
    }
    // revert to last item in renameHistory
    const lastJson = renameHistory[renameHistory.length - 1];
    setJsonInput(lastJson);
    setRenameHistory((prev) => prev.slice(0, -1)); // remove last history
    setRenamedJSON("");
    setCollisions([]);
    setUnusedMappings([]);
    setShowDiff(false);
    setError("");
  };

  // ---------- Use Example ----------
  const useExampleData = () => {
    setJsonInput(exampleJSON);
    setKeyMappingInput(exampleKeyMapping);
    setRenamedJSON("");
    setError("");
    setCopySuccess(false);
    setCollisions([]);
    setUnusedMappings([]);
    setShowDiff(false);
    setRenameHistory([]);
  };

  // ---------- Copy Output ----------
  const handleCopy = () => {
    if (!renamedJSON.trim()) return;
    navigator.clipboard.writeText(renamedJSON);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // ---------- Scroll Sync for text areas ----------
  const syncScrollJson = () => {
    if (jsonTextRef.current && jsonLinesRef.current) {
      jsonLinesRef.current.scrollTop = jsonTextRef.current.scrollTop;
    }
  };
  const syncScrollMapping = () => {
    if (mappingTextRef.current && mappingLinesRef.current) {
      mappingLinesRef.current.scrollTop = mappingTextRef.current.scrollTop;
    }
  };

  // ---------- File Upload (JSON & mapping) ----------
  const handleJsonUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonInput(ev.target.result);
      setError("");
      setCopySuccess(false);
      setCollisions([]);
      setUnusedMappings([]);
      setShowDiff(false);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  const handleMappingUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setKeyMappingInput(ev.target.result);
      setError("");
      setCopySuccess(false);
      setCollisions([]);
      setUnusedMappings([]);
      setShowDiff(false);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  // ---------- Scroll Renamed Output ----------
  const scrollOutputTop = () => {
    if (renamedOutputRef.current) {
      renamedOutputRef.current.scrollTop = 0;
    }
  };
  const scrollOutputBottom = () => {
    if (renamedOutputRef.current) {
      renamedOutputRef.current.scrollTop = renamedOutputRef.current.scrollHeight;
    }
  };

  // ---------- Fix JSON logic (for the main JSON input) ----------
  const fixJSONForMain = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        const repaired = jsonrepair(jsonInput);
        const beautified = jsBeautify(repaired, {
          indent_size: 2,
          brace_style: "collapse",
        });
        const finalJSON = fixAdditionalJSONIssues(beautified);
        if (!finalJSON) {
          throw new Error("Unable to apply additional fixes.");
        }
        setJsonInput(finalJSON);
        setError("");
      } catch (err) {
        setError("Fix JSON failed. Please review manually. " + err.message);
      } finally {
        setCollisions([]);
        setUnusedMappings([]);
        setShowDiff(false);
        setCopySuccess(false);
        setRenamedJSON("");
        setProcessing(false);
      }
    }, 500);
  };

  // ---------- fixAdditionalJSONIssues (like in other components) ----------
  const fixAdditionalJSONIssues = (input) => {
    try {
      let repairedJSON = input;

      // remove duplicates
      const removeDuplicateKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(removeDuplicateKeys);
        } else if (typeof obj === "object" && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => {
            acc[key] = removeDuplicateKeys(obj[key]);
            return acc;
          }, {});
        }
        return obj;
      };

      // fix mismatched braces/brackets
      const openBraces = (repairedJSON.match(/{/g) || []).length;
      const closeBraces = (repairedJSON.match(/}/g) || []).length;
      const openBrackets = (repairedJSON.match(/\[/g) || []).length;
      const closeBrackets = (repairedJSON.match(/\]/g) || []).length;

      if (openBraces > closeBraces) {
        repairedJSON += "}".repeat(openBraces - closeBraces);
      } else if (closeBraces > openBraces) {
        repairedJSON = repairedJSON.slice(0, -(closeBraces - openBraces));
      }
      if (openBrackets > closeBrackets) {
        repairedJSON += "]".repeat(openBrackets - closeBrackets);
      } else if (closeBrackets > openBrackets) {
        repairedJSON = repairedJSON.slice(0, -(closeBrackets - openBrackets));
      }

      // fix True/False/Null
      repairedJSON = repairedJSON.replace(/\b(True|False|Null)\b/g, (m) =>
        m.toLowerCase()
      );

      // fix extra quotes around keys
      repairedJSON = repairedJSON.replace(
        /([{,]\s*)["']([a-zA-Z_][a-zA-Z0-9_]*)["']\s*:/g,
        "$1$2:"
      );
      // fix missing colons
      repairedJSON = repairedJSON.replace(/(".*?")\s*(?=["{])/g, "$1: ");
      // convert numeric strings
      repairedJSON = repairedJSON.replace(/"\b(\d+(\.\d+)?)\b"/g, "$1");

      // parse + remove duplicates
      const parsed = JSON5.parse(repairedJSON);
      const cleaned = removeDuplicateKeys(parsed);

      // re-beautify final
      const final = jsBeautify(JSON.stringify(cleaned), {
        indent_size: 2,
        brace_style: "collapse",
      });
      return final;
    } catch (err) {
      return null;
    }
  };

  // ---------- “Show Diff” side-by-side comparison ----------
  const handleShowDiff = () => {
    if (!renamedJSON || !oldJSONBeforeRename) {
      setError("No data for diff. Perform a rename first.");
      return;
    }
    const dmp = new DiffMatchPatch();
    const patches = dmp.diff_main(oldJSONBeforeRename, renamedJSON);
    dmp.diff_cleanupSemantic(patches);

    // Build minimal HTML
    let html = "";
    patches.forEach(([op, data]) => {
      if (op === 0) {
        // unchanged
        html += `<span>${data.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</span>`;
      } else if (op === 1) {
        // insertion
        html += `<span style="background-color:#ccffcc;">${data
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</span>`;
      } else if (op === -1) {
        // deletion
        html += `<span style="background-color:#ffcccc;text-decoration:line-through;">${data
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</span>`;
      }
    });

    setDiffHtml(html);
    setShowDiff(true);
    setError("");
  };

  // ---------- UI Rendering ----------
  return (
    <div className="container mt-4" style={{ maxWidth: "1000px" }}>
      {/* Dark Mode & Advanced Toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => setShowAdvanced((prev) => !prev)}
        >
          {showAdvanced ? "Hide Advanced Panel" : "Show Advanced Panel"}
        </button>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {showAdvanced && (
        <div
          style={{
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            marginBottom: "10px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <input
              type="checkbox"
              checked={showLineNumbersJson}
              onChange={(e) => setShowLineNumbersJson(e.target.checked)}
            />
            <span>Line # for JSON</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "20px" }}>
            <input
              type="checkbox"
              checked={showLineNumbersMapping}
              onChange={(e) => setShowLineNumbersMapping(e.target.checked)}
            />
            <span>Line # for Mapping</span>
          </label>
          <p style={{ marginTop: "10px", fontSize: "0.85rem", color: "#666" }}>
            Additional advanced settings can be placed here. 
          </p>
        </div>
      )}

      {/* Possibly error/spinner */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div>
          <button className="btn btn-info me-2" onClick={useExampleData}>
            Use Example Data
          </button>
          <button className="btn btn-warning me-2" onClick={fixJSONForMain}>
            Fix JSON
          </button>
          <button className="btn btn-secondary me-2" onClick={handleUndo} title="Undo last rename">
            <FaUndo /> Undo
          </button>
        </div>
        <div style={{ fontSize: "0.85rem", color: "#666", marginTop: "5px" }}>
          <em>
            "Fix JSON" uses <strong>jsonrepair</strong> + additional regex approach.
            Not guaranteed to fix all issues.
          </em>
        </div>
      </div>

      {/* Two columns: JSON input + Key Mapping input */}
      <div className="row mb-3">
        {/* JSON Input column */}
        <div className="col-md-6">
          <label style={{ fontWeight: "bold" }}>JSON Input (object)</label>
          <div style={{ position: "relative", display: "flex", height: "400px" /* bigger area */ }}>
            {showLineNumbersJson && (
              <div
                ref={jsonLinesRef}
                style={{
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                  borderRight: "1px solid #ccc",
                  width: "50px",
                  textAlign: "right",
                  userSelect: "none",
                  color: "#6c757d",
                  paddingTop: "5px",
                }}
              >
                {jsonLines.map((lineNo) => (
                  <div
                    key={lineNo}
                    style={{ height: "1.5em", lineHeight: "1.5em" }}
                  >
                    {lineNo}
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={jsonTextRef}
              className="form-control"
              value={jsonInput}
              onScroll={syncScrollJson}
              onChange={(e) => {
                setJsonInput(e.target.value);
                setError("");
              }}
              style={{
                flex: 1,
                resize: "none",
                borderRadius: 0,
                border: "2px solid #ccc",
                overflowY: "auto",
                fontFamily: "monospace",
                paddingLeft: showLineNumbersJson ? "10px" : "1em",
              }}
              placeholder="Enter JSON object here..."
            />
            {/* Upload icon for JSON */}
            <label
              htmlFor="jsonKeyRenamerUpload"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
              }}
            >
              <FaUpload size={20} />
            </label>
            <input
              id="jsonKeyRenamerUpload"
              type="file"
              style={{ display: "none" }}
              accept=".json"
              onChange={handleJsonUpload}
            />
          </div>
        </div>

        {/* Key Mapping column */}
        <div className="col-md-6">
          <label style={{ fontWeight: "bold" }}>Key Mapping</label>
          <div style={{ position: "relative", display: "flex", height: "400px" /* bigger area */ }}>
            {showLineNumbersMapping && (
              <div
                ref={mappingLinesRef}
                style={{
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                  borderRight: "1px solid #ccc",
                  width: "50px",
                  textAlign: "right",
                  userSelect: "none",
                  color: "#6c757d",
                  paddingTop: "5px",
                }}
              >
                {mappingLines.map((lineNo) => (
                  <div
                    key={lineNo}
                    style={{ height: "1.5em", lineHeight: "1.5em" }}
                  >
                    {lineNo}
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={mappingTextRef}
              className="form-control"
              value={keyMappingInput}
              onScroll={syncScrollMapping}
              onChange={(e) => {
                setKeyMappingInput(e.target.value);
                setError("");
              }}
              style={{
                flex: 1,
                resize: "none",
                borderRadius: 0,
                border: "2px solid #ccc",
                overflowY: "auto",
                fontFamily: "monospace",
                paddingLeft: showLineNumbersMapping ? "10px" : "1em",
              }}
              placeholder='Enter key mapping (e.g. { "oldKey": "newKey" })...'
            />
            {/* Upload icon for Mapping */}
            <label
              htmlFor="jsonKeyRenamerMappingUpload"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
              }}
            >
              <FaUpload size={20} />
            </label>
            <input
              id="jsonKeyRenamerMappingUpload"
              type="file"
              style={{ display: "none" }}
              accept=".json"
              onChange={handleMappingUpload}
            />
          </div>
        </div>
      </div>

      {/* Buttons: rename + clear */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={renameKeys} disabled={processing}>
          {processing ? "Renaming..." : "Rename Keys"}
        </button>
        <button className="btn btn-secondary" onClick={clearInputs}>
          Clear
        </button>
      </div>

      {/* Collisions & Unused summary */}
      {collisions.length > 0 && (
        <div className="alert alert-warning text-center">
          <strong>Collision Detected!</strong> The following new key(s) appeared more than once:{" "}
          {collisions.join(", ")}
        </div>
      )}
      {unusedMappings.length > 0 && (
        <div className="alert alert-info text-center">
          <strong>Unused Mapping Key(s):</strong> {unusedMappings.join(", ")}
        </div>
      )}

      {/* Show Diff button if we have old JSON & new JSON */}
      {oldJSONBeforeRename && renamedJSON && (
        <div className="text-center mb-2">
          <button
            className="btn btn-outline-info"
            onClick={handleShowDiff}
            disabled={!renamedJSON}
          >
            {showDiff ? "Hide Diff" : "Show Diff"}
          </button>
        </div>
      )}

      {/* If we have diff to show */}
      {showDiff && (
        <div
          style={{
            marginBottom: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
            padding: "10px",
            backgroundColor: darkMode ? "#444" : "#f8f9fa",
            maxHeight: "400px",
            overflow: "auto",
          }}
          dangerouslySetInnerHTML={{ __html: diffHtml }}
        />
      )}

      {/* If renamed JSON is ready, show output + scroll/copy */}
      {renamedJSON && !showDiff && (
        <div className="mt-4">
          <h5 className="text-center">Renamed JSON</h5>
          <div
            ref={renamedOutputRef}
            style={{
              position: "relative",
              height: "400px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre",
                fontFamily: "monospace",
              }}
            >
              {renamedJSON}
            </pre>
          </div>

          {/* 2 rows of buttons: scroll top/bottom, then copy */}
          {/* Row 1: Scroll top/bottom */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button className="btn btn-secondary" onClick={scrollOutputTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary" onClick={scrollOutputBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
          </div>

          {/* Row 2: copy output, optionally export/preview/validate */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              <FaCopy /> {copySuccess ? "Copied!" : "Copy Output"}
            </button>

            {/* For completeness, you can add Export, Preview, Validate, etc. here: 
            <button className="btn btn-warning" onClick={...}>
              <FaFileExport /> Export
            </button>
            */}
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONKeyRenamer;